import type { Scene } from "@babylonjs/core/scene";
import type { Camera } from "@babylonjs/core/Cameras/camera";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Action, Combat, Movement, type ArenaDefinition, type AttackDef, type CharacterManifest, type FighterKit } from "@arena/shared";
import { PlayerView, type FighterPose } from "./playerView.ts";

/**
 * The game's look ("2d", the default; D33): a fighter drawn as a 2D sprite standing
 * in the 3D arena, HD-2D style (Octopath Traveler). The sprite always faces
 * the camera (billboard), so the camera stays free; it is mirrored to face
 * left or right on screen, and shows its back when walking away.
 *
 * Everything that is not the body (rings, shadow, name tag, halo, shield,
 * hitbox and Super Slam markers) is the regular `PlayerView`, with its 3D
 * body hidden. The sheets are Kenney's CC0 "Toon Characters", downsampled
 * with a dark outline to look like pixel art: placeholders, not the final art.
 */

/** How a sheet is drawn: every sheet may come from a different tool or artist. */
interface SheetMetrics {
  /** Source pixels per game pixel (2 = the image is pixel art drawn at 2×, or is downsampled by 2). */
  pixel: number;
  /** Draw the 1-pixel dark outline (off when the art has its own). */
  outline: boolean;
  /** The art looks to the left (the standard is to the right). */
  facesLeft: boolean;
  /** Height of the standing character in source pixels: it becomes 1.8 m in the arena. */
  characterHeight: number;
  /** Empty source pixels under the feet in a frame. */
  feet: number;
  /** Name tag height as a share of the standard one, for a drawing that towers over the head (a wig). */
  labelScale?: number;
}

const KENNEY: SheetMetrics = { pixel: 2, outline: true, facesLeft: false, characterHeight: 100, feet: 5 };
const SHEETS: Record<string, SheetMetrics> = {
  // First real character, made with PixelLab: animations `<anim>_<frame>` (docs/STANDARD_SPRITE.md), 1× pixels, own outline.
  dante: { pixel: 1, outline: false, facesLeft: false, characterHeight: 107, feet: 10 },
  mussolini: { pixel:1, outline:false, facesLeft:false, characterHeight:104, feet:12 },
  gandhi: { pixel: 1, outline: false, facesLeft: false, characterHeight: 115, feet: 2 },
  lovecraft:{pixel:1,outline:false,facesLeft:false,characterHeight:112,feet:8},
  'lovecraft-migo':{pixel:1,outline:false,facesLeft:false,characterHeight:108,feet:10},
  'lovecraft-shoggoth':{pixel:1,outline:false,facesLeft:false,characterHeight:88,feet:12},
  kafka:{pixel:1,outline:false,facesLeft:false,characterHeight:107,feet:7},
  'kafka-beetle':{pixel:1,outline:false,facesLeft:false,characterHeight:84,feet:13},
  auramennone:{pixel:1,outline:false,facesLeft:false,characterHeight:122,feet:3},
  'auramennone-hoplite':{pixel:1,outline:false,facesLeft:false,characterHeight:117,feet:5},
  cleopatra:{pixel:1,outline:false,facesLeft:false,characterHeight:109,feet:12},
  // The wig is drawn on top of a body of normal height.
  antonietta:{pixel:1,outline:false,facesLeft:false,characterHeight:105,feet:6,labelScale:1.18},
  'antonietta-wig':{pixel:1,outline:false,facesLeft:false,characterHeight:105,feet:4,labelScale:1.18},
  // The banner rises over her head; on horseback the same pixels, so rider and horse keep their size.
  giovanna:{pixel:1,outline:false,facesLeft:false,characterHeight:102,feet:4},
  'giovanna-horse':{pixel:1,outline:false,facesLeft:false,characterHeight:102,feet:4},
};
const CHARACTER_METRES = 1.8;
/**
 * Walk cycle frames per metre walked: the drawing follows the ground covered,
 * so the feet do not slide (≈ 11 frames per second at full speed).
 */
const WALK_FRAMES_PER_METRE = 1.6;
/**
 * Every character is drawn 1.3 times bigger than its 1.8 m: faces and props
 * read at the game camera distance. The hitbox does not change (D36).
 */
const DISPLAY_SCALE = 1.3;
const OUTLINE = [26, 18, 30];

function metricsOf(sheet: string): SheetMetrics {
  return SHEETS[sheet.replace(/-enhanced$/, "")] ?? KENNEY;
}

/** Animations that can show an action, best first (the first one the sheet has is used). */
const ACTION_ANIMS: Partial<Record<number, readonly string[]>> = {
  [Action.Light1]: ["light1"],
  [Action.Light2]: ["light2", "light1"],
  [Action.Light3]: ["light3", "heavy"],
  [Action.Throw]: ["throw", "heavy", "light3"],
  [Action.Special1]: ["special1", "dash", "heavy"],
  [Action.Special2]: ["special2", "heavy"],
  [Action.AirAttack]: ["airattack", "heavy", "light3"],
  [Action.DashAttack]: ["dashattack", "light2"],
  [Action.SuperSlam]: ["super", "heavy"],
};

export const PROTO_SHEETS = ["male-adventurer", "female-adventurer", "male-person", "female-person", "robot", "zombie"] as const;

interface Atlas {
  url: string;
  width: number;
  height: number;
  frames: Map<string, { x: number; y: number; w: number; h: number }>;
  /** Per animation: first frame that moves and the impact frame (written by the importer). */
  timing: Map<string, { first: number; impact: number }>;
}

const atlases = new Map<string, Promise<Atlas>>();

/** Loads a sheet and its XML, downsamples it and draws a 1-pixel outline: pixel art, roughly. */
function loadAtlas(id: string): Promise<Atlas> {
  let atlas = atlases.get(id);
  if (!atlas) {
    atlas = (async () => {
      const [xml, image] = await Promise.all([
        fetch(`/proto2d/${id}.xml`).then((r) => r.text()),
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = `/proto2d/${id}.png`;
        }),
      ]);
      const m = metricsOf(id);
      const width = Math.round(image.width / m.pixel);
      const height = Math.round(image.height / m.pixel);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      // Already pixel art at an integer scale: sample it, do not blur it.
      ctx.imageSmoothingEnabled = m.outline;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, 0, 0, width, height);
      const data = ctx.getImageData(0, 0, width, height);
      if (m.outline) outline(data.data, width, height);
      ctx.putImageData(data, 0, 0);

      const frames = new Map<string, { x: number; y: number; w: number; h: number }>();
      for (const node of new DOMParser().parseFromString(xml, "text/xml").querySelectorAll("SubTexture")) {
        const n = (k: string) => Number(node.getAttribute(k)) / m.pixel;
        frames.set(node.getAttribute("name")!, { x: n("x"), y: n("y"), w: n("width"), h: n("height") });
      }
      const timing = new Map<string, { first: number; impact: number }>();
      for (const node of new DOMParser().parseFromString(xml, "text/xml").querySelectorAll("Animation")) {
        timing.set(node.getAttribute("name")!, {
          first: Number(node.getAttribute("first") ?? 0),
          impact: Number(node.getAttribute("impact") ?? 0),
        });
      }
      return { url: canvas.toDataURL("image/png"), width, height, frames, timing };
    })();
    atlases.set(id, atlas);
  }
  return atlas;
}

/** Hard alpha, plus a dark pixel around every opaque area: crisp and readable at a distance. */
function outline(px: Uint8ClampedArray, w: number, h: number): void {
  const solid = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    solid[i] = px[i * 4 + 3]! > 110 ? 1 : 0;
    px[i * 4 + 3] = solid[i] ? 255 : 0;
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (solid[i]) continue;
      const near =
        (x > 0 && solid[i - 1]) || (x < w - 1 && solid[i + 1]) || (y > 0 && solid[i - w]) || (y < h - 1 && solid[i + w]);
      if (!near) continue;
      px[i * 4] = OUTLINE[0]!;
      px[i * 4 + 1] = OUTLINE[1]!;
      px[i * 4 + 2] = OUTLINE[2]!;
      px[i * 4 + 3] = 255;
    }
  }
}

let swooshCache: DynamicTexture | null = null;

/** A crescent of pixels, bright at the edge, opening to the right: the arc of a blow. */
function swooshTexture(scene: Scene): DynamicTexture {
  if (swooshCache) return swooshCache;
  const size = 32;
  const texture = new DynamicTexture("swoosh", { width: size, height: size }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
  const image = ctx.createImageData(size, size);
  const c = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x + 0.5 - c * 0.55;
      const dy = y + 0.5 - c;
      const r = Math.hypot(dx, dy) / c;
      const angle = Math.atan2(dy, dx);
      if (Math.abs(angle) > 1.15) continue;
      // Thick in the middle of the arc, thin at the tips.
      const thickness = 0.22 * Math.cos(angle * 1.3);
      const inner = 0.92 - thickness;
      if (r < inner || r > 0.92) continue;
      const edge = (r - inner) / Math.max(0.01, thickness);
      const i = (y * size + x) * 4;
      const bright = edge > 0.6;
      image.data[i] = bright ? 255 : 196;
      image.data[i + 1] = bright ? 250 : 226;
      image.data[i + 2] = bright ? 235 : 255;
      image.data[i + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  texture.update(true);
  texture.hasAlpha = true;
  swooshCache = texture;
  return texture;
}

/** The sheet a champion is drawn with: its own (`/proto2d/<id>.png`), or Dante while it has none yet. */
export function protoSheetFor(championId: string, _colorIndex: number): string {
  return ["dante","gandhi","mussolini","kafka","kafka-beetle","lovecraft","lovecraft-migo","lovecraft-shoggoth","auramennone","auramennone-hoplite","cleopatra","antonietta","antonietta-wig","giovanna","giovanna-horse"].includes(championId) ? `${championId}-enhanced` : championId;
}

/** Warm the small roster before a selection so changing champion never borrows another skin. */
export function preloadProtoSheets(ids:readonly string[]):void {
  for(const id of ids)void loadAtlas(protoSheetFor(id,0)).catch(()=>{});
  if(ids.includes('lovecraft'))for(const id of ['lovecraft-migo','lovecraft-shoggoth'])void loadAtlas(id+'-enhanced').catch(()=>{});
  if(ids.includes('kafka'))void loadAtlas('kafka-beetle-enhanced').catch(()=>{});
  if(ids.includes('auramennone'))void loadAtlas('auramennone-hoplite-enhanced').catch(()=>{});
  if(ids.includes('antonietta'))void loadAtlas('antonietta-wig-enhanced').catch(()=>{});
  if(ids.includes('giovanna'))void loadAtlas('giovanna-horse-enhanced').catch(()=>{});
}

/** The sheet of a champion's timed form, when it has its own drawing. */
const FORM_SHEETS: Readonly<Record<string, string>> = { beetle: "kafka-beetle", wig: "antonietta-wig", horse: "giovanna-horse" };

/** Drawn with this sheet while a champion's own is missing. */
const FALLBACK_SHEET = "dante";
/** Sheets that failed to load: not asked for again (the champion is set every frame). */
const missingSheets = new Set<string>();

const right = new Vector3();
const forward = new Vector3();

export class SpriteFighter {
  readonly base: PlayerView;
  private readonly sprite: Mesh;
  private readonly material: StandardMaterial;
  private readonly camera: Camera;
  private readonly scene: Scene;
  private texture: Texture | null = null;
  private atlas: Atlas | null = null;
  private sheet = "";
  private readonly colorIndex: number;
  private frameName = "";
  private flipped = false;
  /** Animations of a standard sheet (`<anim>_<frame>`) → frame count; empty for the old placeholder sheets. */
  private anims = new Map<string, number>();
  private kit: FighterKit;
  /** Hitstun at the start of the current hit reaction, to play it through. */
  private hitStart = 0;
  private dashTime = -1;
  private metrics: SheetMetrics = KENNEY;
  private koAge = 0;
  private readonly baseScale = new Vector3(1, 1, 1);
  private feetMargin = 0;
  private time = 0;
  private walkPhase = 0;
  /** Camera tilt compensation of the upright card (1 = seen from the side). */
  private tilt = 1;
  /** Metres walked, for the walk cycle. */
  private walkDistance = 0;
  private lastX = 0;
  private lastZ = 0;
  private speed = 0;
  private lastHitstun = 0;
  private lastHitstunSeen = 0;
  /** Hit stop: the drawing holds its pose for a moment when a hit lands (drawing only, the game goes on). */
  private hitStop = 0;
  /** Squash and stretch of the card, 0–1, decaying. */
  private squash = 0;
  private squashSign = 1;
  private shake = 0;
  private readonly swoosh: Mesh;
  private readonly swooshMaterial: StandardMaterial;
  private frameDt = 0;
  private flash = 0;

  constructor(
    scene: Scene,
    arena: ArenaDefinition,
    name: string,
    colorIndex: number,
    isLocal: boolean,
    labels: HTMLElement,
    champion: CharacterManifest,
    kit: FighterKit,
  ) {
    this.scene = scene;
    this.camera = scene.activeCamera!;
    this.colorIndex = colorIndex;
    this.kit = kit;
    this.base = new PlayerView(scene, arena, name, colorIndex, isLocal, labels, champion, kit);
    this.base.hideBody();

    this.sprite = CreatePlane("sprite", { size: 1 }, scene);
    // Origin at the feet, so the billboard tilts around them and never sinks into the floor.
    this.sprite.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
    // Upright card turning around the vertical axis only (Paper Mario): the feet stay on the
    // surface the fighter stands on. Leaning towards the camera would sink them into it.
    this.sprite.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.sprite.isPickable = false;
    this.material = new StandardMaterial(`sprite-${name}`, scene);
    this.material.specularColor = Color3.Black();
    this.material.emissiveColor = new Color3(0.28, 0.26, 0.3);
    this.material.backFaceCulling = false;
    this.material.maxSimultaneousLights = 8;
    this.material.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
    this.material.alphaCutOff = 0.5;
    this.sprite.material = this.material;
    this.sprite.setEnabled(false);

    // Swoosh: a pixel-art arc in front of the fighter while an attack is live.
    this.swoosh = CreatePlane("swoosh", { size: 1 }, scene);
    this.swoosh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    this.swoosh.isPickable = false;
    this.swooshMaterial = new StandardMaterial(`swoosh-${name}`, scene);
    this.swooshMaterial.diffuseTexture = swooshTexture(scene);
    this.swooshMaterial.diffuseTexture.hasAlpha = true;
    this.swooshMaterial.useAlphaFromDiffuseTexture = true;
    this.swooshMaterial.disableLighting = true;
    this.swooshMaterial.emissiveColor = new Color3(1, 1, 1);
    this.swooshMaterial.backFaceCulling = false;
    this.swoosh.material = this.swooshMaterial;
    this.swoosh.setEnabled(false);
    this.setSheet(protoSheetFor(champion.id, colorIndex));
  }

  get position(): Vector3 {
    return this.base.position;
  }

  get label(): HTMLDivElement {
    return this.base.label;
  }

  /** Height of the name tag above the feet: just over the drawn head. */
  get labelHeight(): number {
    if (this.kit.ghost) return 1.6;
    return (CHARACTER_METRES * DISPLAY_SCALE * (this.kit.form==='beetle'?1.3:this.metrics.labelScale ?? 1)) / this.tilt + 0.15;
  }

  chest(out: Vector3): Vector3 {
    return this.base.chest(out);
  }

  setChampion(champion: CharacterManifest, kit: FighterKit): void {
    this.kit = kit;
    this.base.setChampion(champion, kit);
    this.setSheet(protoSheetFor((kit.form && FORM_SHEETS[kit.form]) ?? champion.id, this.colorIndex));
  }

  setTeam(team: number): void {
    this.base.setTeam(team);
  }

  /**
   * A hit landed (server event): the attacker and the target hold their pose
   * for a moment, the attacker stretches into the blow, the target squashes
   * and shakes. Drawing only: positions and timings stay the server's.
   */
  impact(role: "attacker" | "target", strong: boolean): void {
    this.hitStop = Math.max(this.hitStop, strong ? 0.11 : 0.065);
    this.squash = 1;
    this.squashSign = role === "attacker" ? 1 : -1;
    if (role === "target") this.shake = strong ? 0.07 : 0.04;
  }

  setName(name: string): void {
    this.base.setName(name);
  }

  setArena(arena: ArenaDefinition): void {
    this.base.setArena(arena);
  }

  /** A camera flash lights the sprite without hitstop, healing or a hit reaction. */
  illuminate():void {this.flash=Math.max(this.flash,0.12);}

  update(pose: FighterPose, dt: number): void {
    this.base.update(pose, dt);
    this.koAge=pose.knockedOut?this.koAge+dt:0;
    const showKo=this.has("ko") && this.koAge<0.9;
    this.sprite.setEnabled((!pose.knockedOut || showKo) && this.atlas !== null && !this.kit.ghost);
    // A ghost (no champion yet, D49) is the base view's sheet, not a sprite.
    if ((pose.knockedOut && !showKo) || this.kit.ghost) { this.swoosh.setEnabled(false); return; }
    // A lethal hit must start the fall immediately, even during impact hitstop.
    if(pose.knockedOut){this.hitStop=0;this.shake=0;this.squash=0;}
    this.time += dt;
    this.frameDt = dt;

    // Speed from the drawn movement (remote fighters carry no velocity here).
    const moved = Math.hypot(pose.x - this.lastX, pose.z - this.lastZ) / Math.max(dt, 1e-3);
    this.lastX = pose.x;
    this.lastZ = pose.z;
    this.speed += (Math.min(moved, 20) - this.speed) * (1 - Math.exp(-dt * 12));
    this.walkPhase += dt * this.speed * 1.6;
    if (pose.grounded) this.walkDistance += dt * this.speed;

    // Facing on screen: mirrored to the left, back view when walking away from the camera.
    this.camera.getDirectionToRef(Vector3.Right(), right);
    this.camera.getDirectionToRef(Vector3.Forward(), forward);
    const onRight = pose.fx * right.x + pose.fz * right.z;
    const away = (pose.fx * forward.x + pose.fz * forward.z) / Math.max(1e-3, Math.hypot(forward.x, forward.z));
    if (Math.abs(onRight) > 0.15) this.flipped = onRight < 0 !== this.metrics.facesLeft;

    if (this.hitStop > 0) {
      this.hitStop -= dt;
    } else {
      const frame = this.pickFrame(pose, away > 0.7);
      this.showFrame(frame);
    }

    // An upright card seen from above looks squashed: stretch it back by the camera's tilt.
    const tilt = Math.max(0.55, Math.hypot(forward.x, forward.z));
    this.tilt = tilt;
    this.sprite.position.set(pose.x, pose.y - this.feetMargin / tilt, pose.z);
    if (this.hitStop > 0 && this.shake > 0) {
      this.sprite.position.x += (Math.random() - 0.5) * this.shake * 2;
      this.sprite.position.z += (Math.random() - 0.5) * this.shake * 2;
    }
    // Squash and stretch: wider and lower on a blow (attacker stretches forward, target squashes).
    this.squash = Math.max(0, this.squash - dt * 6);
    const k = this.squash * this.squash;
    this.sprite.scaling.x = this.baseScale.x * (1 + 0.16 * k * this.squashSign);
    this.sprite.scaling.y = (this.baseScale.y * (1 - 0.1 * k)) / tilt;
    this.updateSwoosh(pose, right);
    // A small bob while walking, a breath while idle: the sheets have few frames.
    // A light bob, two per walk cycle (one per step), in time with the drawing.
    const cycle = (this.anims.get("walk") ?? 8) / WALK_FRAMES_PER_METRE;
    const bob = this.speed > 0.6 && pose.grounded
      ? Math.abs(Math.sin((this.walkDistance / cycle) * Math.PI * 2)) * 0.025
      : Math.sin(this.time * 3) * 0.008;
    this.sprite.position.y += bob;

    if (pose.hitstun > this.lastHitstun && !pose.frozen) this.flash = 0.14;
    this.lastHitstun = pose.hitstun;
    this.flash = Math.max(0, this.flash - dt);
    const glow = this.flash > 0 ? 1 : 0.28;
    this.material.diffuseColor.set(1, 1, 1);
    if (this.kit.form === "aura" && this.flash === 0) {
      // Auramennone's aura: a warm golden glow that breathes.
      const pulse = 0.62 + Math.sin(this.time * 7) * 0.14;
      this.material.emissiveColor.set(pulse * 1.1, pulse * 0.82, pulse * 0.32);
    } else if (pose.poisoned && this.flash === 0) {
      // Cleopatra's venom: a sickly green that throbs. The light is tinted too,
      // or under a bright arena the green would only wash the sprite out.
      const pulse = 0.5 + 0.5 * Math.sin(this.time * 9);
      this.material.diffuseColor.set(0.5 - pulse * 0.15, 1, 0.45 - pulse * 0.15);
      this.material.emissiveColor.set(0.06, 0.2 + pulse * 0.2, 0.05);
    } else if (pose.charmed && this.flash === 0) {
      this.material.diffuseColor.set(1, 0.7, 0.85);
      this.material.emissiveColor.set(0.38, 0.14, 0.26);
    } else this.material.emissiveColor.set(glow, glow * 0.93, glow * 1.05);
    this.material.alpha = pose.connected ? 1 : 0.45;
  }

  dispose(): void {
    this.base.dispose();
    this.swoosh.dispose();
    this.swooshMaterial.dispose();
    this.sprite.dispose();
    this.material.dispose();
    this.texture?.dispose();
  }

  private setSheet(wanted: string): void {
    const sheet = missingSheets.has(wanted) ? FALLBACK_SHEET : wanted;
    if (sheet === this.sheet) return;
    this.sheet = sheet;
    this.atlas = null;
    this.sprite.setEnabled(false);
    loadAtlas(sheet).then((atlas) => {
      if (this.sheet !== sheet) return;
      this.texture?.dispose();
      const texture = new Texture(atlas.url, this.scene, true, true, Texture.NEAREST_SAMPLINGMODE);
      texture.hasAlpha = true;
      texture.wrapU = Texture.CLAMP_ADDRESSMODE;
      texture.wrapV = Texture.CLAMP_ADDRESSMODE;
      this.texture = texture;
      this.atlas = atlas;
      // Size the card from the idle frame: the standing character is 1.8 m tall whatever the art's resolution.
      const m = metricsOf(sheet);
      const idle = atlas.frames.get("idle")??atlas.frames.get('idle_0');
      if(!idle)throw new Error(`Atlas ${sheet} has no idle frame`);
      const metresPerPixel = (CHARACTER_METRES * DISPLAY_SCALE) / (m.characterHeight / m.pixel);
      this.sprite.scaling.set(idle.w * metresPerPixel, idle.h * metresPerPixel, 1);
      this.baseScale.set(idle.w * metresPerPixel, idle.h * metresPerPixel, 1);
      this.feetMargin = (m.feet / m.pixel) * metresPerPixel;
      this.metrics = m;
      this.anims = new Map();
      for (const key of atlas.frames.keys()) {
        const m = /^([a-z0-9]+)_\d+$/.exec(key);
        if (m) this.anims.set(m[1]!, (this.anims.get(m[1]!) ?? 0) + 1);
      }
      this.material.diffuseTexture = texture;
      this.material.useAlphaFromDiffuseTexture = true;
      this.frameName = "";
      this.sprite.setEnabled(true);
    }).catch(() => {
      // No drawing for this champion yet: draw it as the fallback character.
      missingSheets.add(sheet);
      if (this.sheet !== sheet || sheet === FALLBACK_SHEET) return;
      this.sheet = "";
      this.setSheet(FALLBACK_SHEET);
    });
  }

  /** The pose that tells what the fighter is doing, from the simulation state. */
  private pickFrame(pose: FighterPose, away: boolean): string {
    // Track the hit reaction and the dash, to play their animations from the start.
    if (pose.hitstun > this.lastHitstunSeen) this.hitStart = pose.hitstun;
    this.lastHitstunSeen = pose.hitstun;
    if (pose.dashing) this.dashTime = this.dashTime < 0 ? 0 : this.dashTime + this.frameDt;
    else this.dashTime = -1;
    if (this.anims.size > 0) return this.standardFrame(pose);
    return this.placeholderFrame(pose, away);
  }

  /**
   * Standard sheets (docs/STANDARD_SPRITE.md): every animation is played over
   * the real duration of what it shows — an attack over its startup, active
   * and recovery steps, a hit over its hitstun — so the drawing always matches
   * the game. A missing animation falls back to the closest one.
   */
  private standardFrame(pose: FighterPose): string {
    if(pose.knockedOut && this.has("ko"))return this.at("ko",Math.min(1,this.koAge/0.75));
    if (pose.frozen) return this.loop("idle", 8);
    // Maria Antonietta's whirlwind spins on whatever else happens to her.
    if (pose.spinning && this.has("spin")) return this.loop("spin", 18);
    if(pose.anchorTicks && this.has("hit"))return this.at("hit",.35);
    if (pose.grabbed && this.has("hit")) return this.at("hit",0.5);
    if (pose.hitstun > 0 && this.has("hit")) {
      return this.at("hit", 1 - pose.hitstun / Math.max(1, this.hitStart));
    }
    const move = this.kit.table[pose.action];
    if (move) {
      const anim = ACTION_ANIMS[pose.action]?.find((a) => this.has(a));
      if (anim && move.landingAttack && !pose.grounded) return `${anim}_2`;
      if(anim && this.kit.id==="mussolini" && pose.action===Action.Special1)
        return this.at(anim,pose.actionTick/(move.startup+move.active+move.recovery));
      if(anim && this.kit.id==="mussolini" && pose.action===Action.Special2)
        return this.at("super",pose.actionTick/(move.startup+move.active+move.recovery));
      if (anim) return this.attackFrame(anim, move, pose.actionTick);
    }
    if (pose.dashing && this.has("dash")) {
      return this.at("dash", Math.max(0, this.dashTime) / (Movement.dashTicks / 30));
    }
    if (!pose.grounded && this.has("jump")) {
      // Rising, apex, falling: the take-off frames are left to the ground.
      const t = Math.min(1, Math.max(0, (this.kit.jumpSpeed - pose.vy) / (this.kit.jumpSpeed * 2)));
      const n = this.anims.get("jump")!;
      const first = Math.floor(n / 3);
      return `jump_${Math.min(n - 1, first + Math.floor(t * (n - first)))}`;
    }
    if (this.speed > 0.6 && this.has("walk")) {
      return `walk_${Math.floor(this.walkDistance * WALK_FRAMES_PER_METRE) % this.anims.get("walk")!}`;
    }
    return this.loop("idle", 8);
  }

  /**
   * Attack animation in three beats tied to the real move: the wind-up frames
   * over the startup steps, the impact frame held for the whole live window,
   * then back towards the rest pose over the recovery (the frames after the
   * impact if the drawing has them, else the wind-up played backwards).
   */
  private attackFrame(anim: string, move: AttackDef, tick: number): string {
    const n = this.anims.get(anim)!;
    const t = this.atlas?.timing.get(anim);
    const first = Math.min(t?.first ?? 0, n - 1);
    const impact = Math.min(Math.max(t?.impact ?? n - 1, first), n - 1);
    let frame: number;
    if (tick < move.startup) {
      frame = first + Math.floor((tick / move.startup) * (impact - first));
    } else if (tick < move.startup + move.active) {
      frame = impact;
    } else {
      const progress = (tick - move.startup - move.active) / Math.max(1, move.recovery);
      const tail = n - 1 - impact;
      frame = tail > 0
        ? impact + 1 + Math.min(tail - 1, Math.floor(progress * tail))
        : impact - Math.floor(progress * (impact - first + 1));
    }
    return `${anim}_${Math.min(n - 1, Math.max(0, frame))}`;
  }

  /** The swoosh arc while a melee attack is live, a bit past it, fading. */
  private updateSwoosh(pose: FighterPose, screenRight: Vector3): void {
    const move = this.kit.table[pose.action];
    const dedicatedArt = this.kit.id === "mussolini" || this.kit.id === "gandhi" || (this.kit.id === "dante" && pose.action !== Action.Light1);
    const live = move !== undefined && !move.projectile && pose.action !== Action.SuperSlam && !dedicatedArt;
    const from = live ? move.startup : 0;
    const until = live ? move.startup + move.active + 3 : 0;
    const visible = live && pose.actionTick >= from && pose.actionTick < until && !pose.frozen;
    this.swoosh.setEnabled(visible);
    if (!visible) return;
    const fade = 1 - (pose.actionTick - from) / Math.max(1, until - from);
    const size = 0.9 + move.range * 0.9;
    const side = this.flipped !== this.metrics.facesLeft ? -1 : 1;
    // In front of the body on screen, at chest height.
    this.swoosh.position.set(
      pose.x + screenRight.x * side * (0.35 + move.range * 0.45),
      pose.y + Movement.height * 0.62 + (move.lift > 0 ? 0.25 : 0),
      pose.z + screenRight.z * side * (0.35 + move.range * 0.45),
    );
    this.swoosh.scaling.set(size * side, size * (move.lift > 0 ? 1.25 : 0.85), 1);
    this.swooshMaterial.alpha = 0.85 * fade;
  }

  private has(anim: string): boolean {
    return (this.anims.get(anim) ?? 0) > 0;
  }

  /** Frame of `anim` at `progress` (0–1) through it. */
  private at(anim: string, progress: number): string {
    const n = this.anims.get(anim)!;
    return `${anim}_${Math.min(n - 1, Math.max(0, Math.floor(progress * n)))}`;
  }

  /** A looping animation at `fps`. */
  private loop(anim: string, fps: number): string {
    const n = this.anims.get(anim) ?? 0;
    return n > 0 ? `${anim}_${Math.floor(this.time * fps) % n}` : "idle";
  }

  /** The placeholder sheets (Kenney): single poses by name. */
  private placeholderFrame(pose: FighterPose, away: boolean): string {
    if (pose.frozen) return "idle";
    if (pose.hitstun > 0) return pose.grounded ? "hurt" : "fallDown";
    switch (pose.action) {
      case Action.Light1:
        return pose.actionTick < 3 ? "idle" : "attack0";
      case Action.Light2:
        return pose.actionTick < 3 ? "attack0" : "attack1";
      case Action.Light3:
        return pose.actionTick < 4 ? "duck" : "attack2";
      case Action.Throw:
        return pose.actionTick < 8 ? "behindBack" : "attackKick";
      case Action.Special1:
        return pose.actionTick < 3 ? "shoveBack" : "shove";
      case Action.Special2:
        return pose.actionTick < 6 ? "behindBack" : "show";
      case Action.SuperSlam:
        return pose.actionTick < Combat.superStartup ? (Math.floor(this.time * 10) % 2 ? "cheer0" : "cheer1") : "wide";
    }
    if (pose.dashing) return "slide";
    if (!pose.grounded) return pose.vy > 0 ? "jump" : "fall";
    if (this.speed > 0.6) {
      if (away) return "back";
      if (this.speed > 6.5) return `run${Math.floor(this.walkPhase * 1.5) % 3}`;
      return `walk${Math.floor(this.walkPhase * 4) % 8}`;
    }
    return away ? "back" : "idle";
  }

  private showFrame(name: string): void {
    const atlas = this.atlas;
    const texture = this.texture;
    if (!atlas || !texture) return;
    const key = `${name}|${this.flipped}`;
    if (key === this.frameName) return;
    this.frameName = key;
    const f = atlas.frames.get(name) ?? atlas.frames.get("idle")!;
    texture.uScale = (this.flipped ? -f.w : f.w) / atlas.width;
    texture.uOffset = (this.flipped ? f.x + f.w : f.x) / atlas.width;
    texture.vScale = f.h / atlas.height;
    texture.vOffset = 1 - (f.y + f.h) / atlas.height;
  }
}

/**
 * A champion as a marble statue on its pedestal in the museum (D49): its idle
 * frame turned to cool greys, standing still. No new drawing is needed, so
 * every champion added later has its statue at once.
 */
export class StatueSprite {
  private readonly scene: Scene;
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private texture: Texture | null = null;
  private height = 0;
  private feet = 0;
  private visible = true;
  private readonly base: Vector3;

  constructor(scene: Scene, championId: string, x: number, y: number, z: number) {
    this.scene = scene;
    this.base = new Vector3(x, y, z);
    this.mesh = CreatePlane(`statue-${championId}`, { size: 1 }, scene);
    this.mesh.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
    this.mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable = false;
    this.mesh.setEnabled(false);
    this.material = new StandardMaterial(`statue-${championId}`, scene);
    this.material.specularColor = Color3.Black();
    this.material.emissiveColor = new Color3(0.32, 0.32, 0.35);
    this.material.backFaceCulling = false;
    this.material.maxSimultaneousLights = 8;
    this.material.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST;
    this.material.alphaCutOff = 0.5;
    this.mesh.material = this.material;
    void this.carve(protoSheetFor(championId, 0));
  }

  /** Taken by a player: the pedestal stays empty. */
  setVisible(on: boolean): void {
    this.visible = on;
    this.mesh.setEnabled(on && this.texture !== null);
  }

  /** An upright card seen from above looks squashed: stretched back by the camera's tilt, like the fighters. */
  update(camera: Camera): void {
    camera.getDirectionToRef(Vector3.Forward(), forward);
    const tilt = Math.max(0.55, Math.hypot(forward.x, forward.z));
    this.mesh.scaling.y = this.height / tilt;
    this.mesh.position.set(this.base.x, this.base.y - this.feet / tilt, this.base.z);
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture?.dispose();
  }

  private async carve(sheet: string): Promise<void> {
    const atlas = await loadAtlas(sheet).catch(() => loadAtlas(FALLBACK_SHEET));
    const f = atlas.frames.get("idle") ?? atlas.frames.get("idle_0");
    if (!f) return;
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = atlas.url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = f.w;
    canvas.height = f.h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(image, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
    const pixels = ctx.getImageData(0, 0, f.w, f.h);
    const px = pixels.data;
    for (let i = 0; i < px.length; i += 4) {
      const lum = 0.3 * px[i]! + 0.59 * px[i + 1]! + 0.11 * px[i + 2]!;
      // The outline stays dark; the rest is marble, a little blue.
      const v = lum < 45 ? lum * 0.9 + 18 : 78 + lum * 0.68;
      px[i] = v * 0.95;
      px[i + 1] = v * 0.97;
      px[i + 2] = Math.min(255, v * 1.06);
    }
    ctx.putImageData(pixels, 0, 0);
    const texture = new Texture(canvas.toDataURL("image/png"), this.scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    texture.hasAlpha = true;
    this.texture = texture;
    this.material.diffuseTexture = texture;
    this.material.useAlphaFromDiffuseTexture = true;
    const m = metricsOf(sheet);
    const metresPerPixel = (CHARACTER_METRES * DISPLAY_SCALE) / (m.characterHeight / m.pixel);
    this.mesh.scaling.x = f.w * metresPerPixel;
    this.height = f.h * metresPerPixel;
    this.feet = (m.feet / m.pixel) * metresPerPixel;
    this.mesh.setEnabled(this.visible);
  }
}
